import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { ButtonHTMLAttributes, forwardRef } from "react";

import { ButtonSpinner, StyledButton } from "./Button.styled";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /**
   * Set button spinner to be loading state with spinner
   */
  activated?: boolean;

  /**
   * Set button color
   *
   * @type {"orange" | "green" | "grey" | "red"}
   * @default {"orange"}
   */
  color?: "orange" | "green" | "grey" | "red";

  /**
   * Set button icon
   */
  icon?: IconProp;

  /**
   * Set button icon position
   * @default "left"
   */
  iconPosition?: "left" | "right";

  /**
   * Set button style to outline mode (white inside, only border)
   * @default {true}
   */
  outline?: boolean;

  /**
   * Set button variant
   *
   * @type {"primary" | "secondary" | "tertiary" | "mini"}
   * @default {"primary"}
   */
  variant?: "primary" | "secondary" | "tertiary" | "mini";

  /**
   * Set button to be full width
   * @default {false}
   */
  block?: boolean;

  /**
   * Set button type
   * @default {"button"}
   */
  type?: string;

  /**
   * Set button disabled
   */
  disabled?: boolean;
};

const defaultProps: ButtonProps = {
  activated: false,
  color: "orange",
  icon: null as null,
  outline: false,
  variant: "primary",
  block: false,
  type: "button"
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    children,
    className,
    activated,
    color,
    icon,
    outline,
    variant,
    block,
    type,
    disabled,
    iconPosition,
    ...restProps
  } = props;
  const buttonClass = classNames("c-button", { "is-only-icon": icon !== null && children === undefined }, className);

  return (
    <StyledButton
      data-testid="qa-button"
      ref={ref}
      className={buttonClass}
      color={color}
      outline={outline}
      variant={variant}
      block={block}
      type={type}
      disabled={disabled}
      {...restProps}
    >
      {icon || activated ? (
        <>
          <span>
            {activated && (
              <ButtonSpinner
                data-testid="qa-button-spinner"
                className="c-button-spinner"
                color={color}
                outline={outline}
                variant={variant}
                disabled={disabled}
              />
            )}
            {icon && !activated && iconPosition !== "right" && (
              <FontAwesomeIcon icon={icon} data-testid="qa-button-icon" className="c-button__icon" />
            )}
          </span>
          {children && <span>{children}</span>}
          {icon && !activated && iconPosition === "right" && (
            <span className="u-ml-1">
              <FontAwesomeIcon icon={icon} data-testid="qa-button-icon" className="c-button__icon" />
            </span>
          )}
        </>
      ) : (
        children
      )}
    </StyledButton>
  );
});

Button.defaultProps = defaultProps;

export default Button;
